import React, { useContext, useRef, useState } from "react";
import HelmetComponent from "../components/common/HelmetComponent";
import emailjs from "@emailjs/browser";
import contactImage from "../images/contact1.png";
import "../styles/contact.css";
import { WebsiteContext } from "../context/AppContext";
import Loader from "react-js-loader";
import { HiUser, HiEnvelope, HiChatBubbleBottomCenterText, HiClock } from "react-icons/hi2";
import { IoPaperPlane, IoCheckmarkCircle } from "react-icons/io5";
import { FaWhatsapp } from "react-icons/fa6";
import { motion } from "framer-motion";

const ContactUs = () => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(false);
  const [error, setError] = useState("");
  const form = useRef();
  const { color, dark } = useContext(WebsiteContext);

  const sendEmail = (e) => {
    e.preventDefault();
    if (!username || !email || !message) {
      setError("Please fill in your name, email, and message.");
      return;
    }

    setError("");
    setLoading(true);

    emailjs
      .sendForm(
        "service_reufico",
        "template_auy4oho",
        form.current,
        "94QRcCXq7uSLVqVlb"
      )
      .then(
        () => {
          setLoading(false);
          setStatus(true);
          setUsername("");
          setEmail("");
          setMessage("");
        },
        (err) => {
          console.error("EmailJS Error:", err);
          setLoading(false);
          setError("Failed to send message. Please try again or reach out on Instagram.");
        }
      );
  };

  return (
    <>
      <HelmetComponent
        title="Contact Us | Fluent Fable"
        name="description"
        content="Reach out to the Fluent Fable team for questions about our English learning app, live spoken English masterclasses, or partnerships."
      />

      <div className="contact_page_wrapper">
        {/* Header */}
        <div className="contact_hero_header">
          <span className={`contact_badge ${dark ? "contact_badge_dark" : "contact_badge_light"}`}>
            ✉️ Get in Touch
          </span>
          <h1 className="contact_main_title" style={{ color: color }}>
            We'd Love to Hear From You
          </h1>
          <p className="contact_main_subtitle" style={{ color: color }}>
            Have questions about our AI features, our 1-Month Live Spoken English Program, or suggestions for the app? Drop us a line below.
          </p>
        </div>

        {/* Main Grid / Success Box */}
        {!status ? (
          <div className="contact_grid_container">
            {/* Left Info Column */}
            <div className="contact_info_column">
              <div className={`contact_info_card ${dark ? "info_card_dark" : "info_card_light"}`}>
                <div className="info_icon_bubble bubble_blue">
                  <HiEnvelope />
                </div>
                <div>
                  <h3 className="info_text_title" style={{ color: color }}>
                    Email Inquiries
                  </h3>
                  <p className="info_text_desc" style={{ color: color }}>
                    Send direct queries anytime. We aim to respond to all learners within 24 hours.
                  </p>
                </div>
              </div>

              <div className={`contact_info_card ${dark ? "info_card_dark" : "info_card_light"}`}>
                <div className="info_icon_bubble bubble_green">
                  <FaWhatsapp />
                </div>
                <div>
                  <h3 className="info_text_title" style={{ color: color }}>
                    Learner Community
                  </h3>
                  <p className="info_text_desc" style={{ color: color }}>
                    Active students get access to exclusive WhatsApp group discussions and live mentoring.
                  </p>
                </div>
              </div>

              <div className={`contact_info_card ${dark ? "info_card_dark" : "info_card_light"}`}>
                <div className="info_icon_bubble bubble_purple">
                  <HiClock />
                </div>
                <div>
                  <h3 className="info_text_title" style={{ color: color }}>
                    Support Hours
                  </h3>
                  <p className="info_text_desc" style={{ color: color }}>
                    Monday – Saturday, 9:00 AM – 7:00 PM IST.
                  </p>
                </div>
              </div>

              {/* <div className="contact_illustration_wrap">
                <img src={contactImage} alt="Contact Support Illustration" />
              </div> */}
            </div>

            {/* Right Form Column */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className={`contact_form_card ${dark ? "form_card_dark" : "form_card_light"}`}
            >
              <form ref={form} onSubmit={sendEmail}>
                {/* Name */}
                <div className="form_field_group">
                  <label className="form_label" style={{ color: color }}>
                    Your Name
                  </label>
                  <div className="form_input_wrap">
                    <HiUser className="form_input_icon" style={{ color: color }} />
                    <input
                      type="text"
                      name="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="e.g. Manikandan"
                      className="modern_input"
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="form_field_group">
                  <label className="form_label" style={{ color: color }}>
                    Email Address
                  </label>
                  <div className="form_input_wrap">
                    <HiEnvelope className="form_input_icon" style={{ color: color }} />
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@example.com"
                      className="modern_input"
                      required
                    />
                  </div>
                </div>

                {/* Message */}
                <div className="form_field_group">
                  <label className="form_label" style={{ color: color }}>
                    Your Message
                  </label>
                  <textarea
                    name="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Tell us what you need help with or ask about our spoken English program..."
                    className="modern_textarea"
                    required
                  />
                </div>

                {/* Error */}
                {error && <div className="form_error_alert">{error}</div>}

                {/* Loading or Submit */}
                {loading ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: "16px 0" }}>
                    <Loader type="spinner-cub" size={36} bgColor={color} color={color} />
                  </div>
                ) : (
                  <button type="submit" className="contact_submit_btn">
                    <IoPaperPlane size={17} />
                    <span>Send Message</span>
                  </button>
                )}
              </form>
            </motion.div>
          </div>
        ) : (
          /* Success Screen */
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`contact_success_card ${dark ? "success_card_dark" : "success_card_light"}`}
          >
            <IoCheckmarkCircle className="success_icon_big" />
            <h2 className="success_title" style={{ color: color }}>
              Message Received!
            </h2>
            <p className="success_desc" style={{ color: color }}>
              Thank you for reaching out to Fluent Fable. Our team has received your inquiry and will respond to your email as soon as possible.
            </p>
            <button
              className="contact_submit_btn"
              style={{ maxWidth: 220, margin: "0 auto" }}
              onClick={() => setStatus(false)}
            >
              Send Another Message
            </button>
          </motion.div>
        )}
      </div>
    </>
  );
};

export default ContactUs;
